import React, { useCallback, useMemo } from 'react';
import moment from 'moment-timezone';
import { FrequencyType, CronConfig } from '../../../../types';
import SingleSelect, { SelectOption } from '../../../../../../components/common/Search/SingleSelect';
import NextTriggerPreview from './NextTriggerPreview';

interface CronSchedulerProps {
    frequency: FrequencyType;
    cronConfig: CronConfig;
    onCronChange: (config: CronConfig) => void;
}

const DAYS_OF_WEEK: SelectOption[] = [
    { value: '0', label: 'Sunday' },
    { value: '1', label: 'Monday' },
    { value: '2', label: 'Tuesday' },
    { value: '3', label: 'Wednesday' },
    { value: '4', label: 'Thursday' },
    { value: '5', label: 'Friday' },
    { value: '6', label: 'Saturday' },
];

function getOrdinalSuffix(n: number): string {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return s[(v - 20) % 10] || s[v] || s[0];
}

const DAYS_OF_MONTH: SelectOption[] = Array.from({ length: 31 }, (_, i) => ({
    value: String(i + 1),
    label: `${i + 1}${getOrdinalSuffix(i + 1)}`,
}));

// Generate hours with timezone-aware display
const generateHourOptions = (timezone: string): SelectOption[] => {
    return Array.from({ length: 24 }, (_, hour) => {
        // Create a moment in the specified timezone to format the hour
        const m = moment.tz({ hour, minute: 0 }, timezone);
        const label = m.format('h A'); // e.g., "9 AM"
        return {
            value: String(hour),
            label,
        };
    });
};

// Generate minutes 0-59
const MINUTES: SelectOption[] = Array.from({ length: 60 }, (_, i) => ({
    value: String(i),
    label: i < 10 ? `0${i}` : `${i}`,
}));

// Get all available timezones from moment-timezone, grouped by region
const getTimezoneOptions = (): SelectOption[] => {
    const userTimezone = moment.tz.guess();
    const commonZones = [
        // Universal / GMT
        'UTC',
        'Etc/GMT',
        'Etc/GMT+0',

        // Americas - North
        'America/New_York',      // Eastern Time
        'America/Chicago',       // Central Time
        'America/Denver',        // Mountain Time
        'America/Phoenix',       // Arizona (no DST)
        'America/Los_Angeles',   // Pacific Time
        'America/Anchorage',     // Alaska
        'Pacific/Honolulu',      // Hawaii
        'America/Toronto',       // Canada Eastern
        'America/Vancouver',     // Canada Pacific

        // Americas - Central & South
        'America/Mexico_City',   // Mexico
        'America/Bogota',        // Colombia
        'America/Lima',          // Peru
        'America/Santiago',      // Chile
        'America/Sao_Paulo',     // Brazil
        'America/Buenos_Aires',  // Argentina

        // Europe
        'Europe/London',         // UK / GMT
        'Europe/Dublin',         // Ireland
        'Europe/Paris',          // France
        'Europe/Berlin',         // Germany
        'Europe/Amsterdam',      // Netherlands
        'Europe/Brussels',       // Belgium
        'Europe/Madrid',         // Spain
        'Europe/Rome',           // Italy
        'Europe/Zurich',         // Switzerland
        'Europe/Vienna',         // Austria
        'Europe/Warsaw',         // Poland
        'Europe/Stockholm',      // Sweden
        'Europe/Oslo',           // Norway
        'Europe/Copenhagen',     // Denmark
        'Europe/Helsinki',       // Finland
        'Europe/Moscow',         // Russia
        'Europe/Istanbul',       // Turkey
        'Europe/Athens',         // Greece

        // Middle East
        'Asia/Dubai',            // UAE
        'Asia/Riyadh',           // Saudi Arabia
        'Asia/Jerusalem',        // Israel
        'Asia/Tehran',           // Iran
        'Asia/Qatar',            // Qatar
        'Asia/Kuwait',           // Kuwait

        // Asia - South
        'Asia/Kolkata',          // India
        'Asia/Colombo',          // Sri Lanka
        'Asia/Karachi',          // Pakistan
        'Asia/Dhaka',            // Bangladesh
        'Asia/Kathmandu',        // Nepal

        // Asia - Southeast
        'Asia/Singapore',        // Singapore
        'Asia/Bangkok',          // Thailand
        'Asia/Jakarta',          // Indonesia
        'Asia/Kuala_Lumpur',     // Malaysia
        'Asia/Manila',           // Philippines
        'Asia/Ho_Chi_Minh',      // Vietnam

        // Asia - East
        'Asia/Tokyo',            // Japan
        'Asia/Seoul',            // South Korea
        'Asia/Shanghai',         // China
        'Asia/Hong_Kong',        // Hong Kong
        'Asia/Taipei',           // Taiwan

        // Australia & Oceania
        'Australia/Sydney',      // NSW (AEDT/AEST)
        'Australia/Melbourne',   // Victoria
        'Australia/Brisbane',    // Queensland (no DST)
        'Australia/Adelaide',    // South Australia
        'Australia/Perth',       // Western Australia
        'Australia/Darwin',      // Northern Territory
        'Australia/Hobart',      // Tasmania
        'Pacific/Auckland',      // New Zealand
        'Pacific/Fiji',          // Fiji

        // Africa
        'Africa/Johannesburg',   // South Africa
        'Africa/Cairo',          // Egypt
        'Africa/Lagos',          // Nigeria
        'Africa/Nairobi',        // Kenya
        'Africa/Casablanca',     // Morocco
    ];

    // Build options with user's timezone first if not in common list
    const options: SelectOption[] = [];

    // Add user's timezone at the top if detected
    if (userTimezone && !commonZones.includes(userTimezone)) {
        const offset = moment.tz(userTimezone).format('Z');
        options.push({
            value: userTimezone,
            label: `${userTimezone.replace(/_/g, ' ')} (${offset}) - Your timezone`,
        });
    }

    // Add common timezones with their current offset
    commonZones.forEach(tz => {
        try {
            const offset = moment.tz(tz).format('Z');
            const abbr = moment.tz(tz).format('z');

            // Custom display names for better readability
            let displayName: string;
            if (tz === 'UTC') {
                displayName = `UTC - Coordinated Universal Time (${offset})`;
            } else if (tz === 'Etc/GMT' || tz === 'Etc/GMT+0') {
                displayName = `GMT - Greenwich Mean Time (${offset})`;
            } else {
                // Get city name and add region context
                const parts = tz.split('/');
                const city = parts.pop()?.replace(/_/g, ' ') || tz;
                const region = parts.length > 0 ? parts[0] : '';

                // Add helpful context for major cities
                const contextMap: Record<string, string> = {
                    'New_York': 'Eastern Time',
                    'Chicago': 'Central Time',
                    'Denver': 'Mountain Time',
                    'Los_Angeles': 'Pacific Time',
                    'Phoenix': 'Arizona',
                    'Anchorage': 'Alaska',
                    'Honolulu': 'Hawaii',
                    'London': 'UK',
                    'Dublin': 'Ireland',
                    'Paris': 'France',
                    'Berlin': 'Germany',
                    'Moscow': 'Russia',
                    'Dubai': 'UAE',
                    'Kolkata': 'India',
                    'Shanghai': 'China',
                    'Tokyo': 'Japan',
                    'Sydney': 'NSW',
                    'Melbourne': 'Victoria',
                    'Brisbane': 'Queensland',
                    'Perth': 'Western Australia',
                    'Adelaide': 'South Australia',
                    'Auckland': 'New Zealand',
                };

                const cityKey = city.replace(/ /g, '_');
                const context = contextMap[cityKey];
                displayName = context
                    ? `${city} - ${context} (${abbr} ${offset})`
                    : `${city} (${abbr} ${offset})`;
            }

            const isUserTz = tz === userTimezone;
            options.push({
                value: tz,
                label: isUserTz ? `${displayName} ★` : displayName,
            });
        } catch (e) {
            // Skip invalid timezone
        }
    });

    return options;
};

const TIMEZONE_OPTIONS = getTimezoneOptions();

const CronScheduler: React.FC<CronSchedulerProps> = ({
    frequency,
    cronConfig,
    onCronChange,
}) => {
    // Don't show for immediate frequency
    if (frequency === 'immediate') {
        return null;
    }

    // Generate hour options based on selected timezone
    const hourOptions = useMemo(() => {
        return generateHourOptions(cronConfig.timezone);
    }, [cronConfig.timezone]);

    const handleTimezoneChange = useCallback((option: SelectOption | null) => {
        if (option) {
            onCronChange({ ...cronConfig, timezone: option.value });
        }
    }, [cronConfig, onCronChange]);

    const handleDayChange = useCallback((option: SelectOption | null) => {
        if (option) {
            onCronChange({ ...cronConfig, day: parseInt(option.value, 10) });
        }
    }, [cronConfig, onCronChange]);

    const handleHourChange = useCallback((option: SelectOption | null) => {
        if (option) {
            onCronChange({ ...cronConfig, hour: parseInt(option.value, 10) });
        }
    }, [cronConfig, onCronChange]);

    const handleMinuteChange = useCallback((option: SelectOption | null) => {
        if (option) {
            onCronChange({ ...cronConfig, minute: parseInt(option.value, 10) });
        }
    }, [cronConfig, onCronChange]);

    const showDayOfWeek = frequency === 'weekly' || frequency === 'biweekly';
    const showDayOfMonth = frequency === 'monthly';
    const showDaySelector = showDayOfWeek || showDayOfMonth;

    const dayOptions = useMemo(() => {
        if (showDayOfWeek) return DAYS_OF_WEEK;
        if (showDayOfMonth) return DAYS_OF_MONTH;
        return [];
    }, [showDayOfWeek, showDayOfMonth]);

    const selectedTimezoneOption = useMemo(() => {
        const found = TIMEZONE_OPTIONS.find(opt => opt.value === cronConfig.timezone);
        if (found) return found;

        // If timezone is valid but not in our list, create an option for it
        try {
            const offset = moment.tz(cronConfig.timezone).format('Z');
            return {
                value: cronConfig.timezone,
                label: `${cronConfig.timezone.replace(/_/g, ' ')} (${offset})`,
            };
        } catch (e) {
            return TIMEZONE_OPTIONS[0]; // Fallback to first option (usually user's timezone or UTC)
        }
    }, [cronConfig.timezone]);

    const selectedDayOption = useMemo(() => {
        return dayOptions.find(opt => opt.value === String(cronConfig.day)) || null;
    }, [dayOptions, cronConfig.day]);

    const selectedHourOption = useMemo(() => {
        return hourOptions.find(opt => opt.value === String(cronConfig.hour)) || null;
    }, [hourOptions, cronConfig.hour]);

    const selectedMinuteOption = useMemo(() => {
        return MINUTES.find(opt => opt.value === String(cronConfig.minute)) || null;
    }, [cronConfig.minute]);

    const getFrequencyDescription = () => {
        switch (frequency) {
            case 'daily':
                return 'Sent every day at the specified time.';
            case 'weekly':
                return 'Sent once a week on the selected day and time.';
            case 'biweekly':
                return 'Sent every two weeks on the selected day and time.';
            case 'monthly':
                return 'Sent once a month on the selected day and time.';
            default:
                return '';
        }
    };

    // Format current selection for display
    const getCurrentTimeDisplay = () => {
        const m = moment.tz({ hour: cronConfig.hour, minute: cronConfig.minute }, cronConfig.timezone);
        return m.format('h:mm A');
    };

    return (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 shadow-sm space-y-4">
            <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Schedule Time
                </label>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                    {getFrequencyDescription()}
                </span>
            </div>

            {/* Timezone Selector - FIRST */}
            <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
                    Timezone
                </label>
                <SingleSelect
                    name="cron-timezone"
                    options={TIMEZONE_OPTIONS}
                    placeholder="Select timezone..."
                    onChange={handleTimezoneChange}
                    value={selectedTimezoneOption}
                    width="w-full"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    All times below are shown in this timezone
                </p>
            </div>

            {/* Time Selection Row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Day Selector (for weekly/biweekly/monthly) */}
                {showDaySelector && (
                    <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
                            {showDayOfWeek ? 'Day of Week' : 'Day of Month'}
                        </label>
                        <SingleSelect
                            name="cron-day"
                            options={dayOptions}
                            placeholder="Select day..."
                            onChange={handleDayChange}
                            value={selectedDayOption}
                            width="w-full"
                        />
                    </div>
                )}

                {/* Hour Selector */}
                <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
                        Hour
                    </label>
                    <SingleSelect
                        name="cron-hour"
                        options={hourOptions}
                        placeholder="Select hour..."
                        onChange={handleHourChange}
                        value={selectedHourOption}
                        width="w-full"
                    />
                </div>

                {/* Minute Selector */}
                <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
                        Minute
                    </label>
                    <SingleSelect
                        name="cron-minute"
                        options={MINUTES}
                        placeholder="Select minute..."
                        onChange={handleMinuteChange}
                        value={selectedMinuteOption}
                        width="w-full"
                    />
                </div>
            </div>

            {/* Current Selection Summary */}
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 flex items-center gap-2">
                <svg className="w-4 h-4 text-primary-light dark:text-primary-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-sm text-gray-700 dark:text-gray-300">
                    Scheduled for <strong>{getCurrentTimeDisplay()}</strong>
                    {showDayOfWeek && selectedDayOption && <> on <strong>{selectedDayOption.label}s</strong></>}
                    {showDayOfMonth && selectedDayOption && <> on the <strong>{selectedDayOption.label}</strong></>}
                    <span className="text-gray-500 dark:text-gray-400"> ({selectedTimezoneOption?.label?.split(' (')[0]})</span>
                </span>
            </div>

            {/* Next Trigger Preview */}
            <NextTriggerPreview
                frequency={frequency}
                cronConfig={cronConfig}
            />
        </div>
    );
};

export default CronScheduler;
